import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, WhatsappLogo } from "@phosphor-icons/react";
import { BLOG_POSTS } from "@/data/blog";
import { whatsappLink } from "@/data/site";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) document.title = `${post.title} | Ruta 360`;
  }, [post]);

  if (!post) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <main className="pt-[140px] max-w-3xl mx-auto px-6 py-20">
          <h1 className="font-display text-4xl uppercase font-semibold">Artículo no encontrado</h1>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="bg-white min-h-screen">
      <Header />
      <main className="pt-[100px]">
        <article className="max-w-3xl mx-auto px-6 py-14">
          <button
            data-testid="blog-back-button"
            onClick={() => navigate("/blog")}
            className="group inline-flex items-center gap-2 text-[11px] text-black/50 hover:text-black transition-colors mb-10"
          >
            <ArrowLeft size={15} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Volver al blog
          </button>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-[11px] text-[#FF5508] mb-4">
              {post.category} · {new Date(post.date).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
            </p>
            <h1 data-testid="blog-post-title" className="font-display text-4xl sm:text-5xl uppercase font-semibold leading-tight tracking-tight text-[#111418] mb-8">
              {post.title}
            </h1>
          </motion.div>

          <motion.img
            initial={{ opacity: 0, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            src={post.image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover border border-black/10 mb-10"
          />

          <div className="flex flex-col gap-6">
            {post.content.map((paragraph, i) => (
              <p key={i} className={`text-black/70 leading-relaxed ${i === 0 ? "text-lg font-medium text-black/85" : "text-base"}`}>
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-14 border border-black/10 bg-[#F4F5F6] p-8 text-center">
            <p className="font-display text-2xl uppercase font-semibold text-[#111418] mb-2">
              ¿Te falta equipo para esta ruta?
            </p>
            <p className="text-black/55 text-sm mb-6">
              Escríbenos y te armamos el kit exacto con envío a todo México.
            </p>
            <a
              data-testid="blog-whatsapp-cta"
              href={whatsappLink(`Hola Ruta 360, leí su guía "${post.title}" y quiero más información sobre el equipo.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white text-xs px-8 py-4 transition-colors duration-300 hover:bg-black"
            >
              <WhatsappLogo size={16} weight="fill" /> Pregúntanos por WhatsApp
            </a>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};
